import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { Research, ResearchTag } from '../../../../types/Research'
import {
  addResearch,
  addResearchTag,
  fetchResearchTags,
} from '../../../../utils/researchUtils'
import useStore from '../../../../store/useStore'
import useCanManageResearch from '../../../../hooks/useCanManageResearch'

export interface AddResearchFormData {
  title: string
  publicationYear: string
  journal: string
  url: string
  doi: string
  abstract: string
  researcherNotes: string
}

export const useAddResearch = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const user = useStore((s) => s.user)
  const canManageResearch = useCanManageResearch()

  // State management
  const [tags, setTags] = useState<ResearchTag[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [newTagName, setNewTagName] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showTagError, setShowTagError] = useState(false)

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddResearchFormData>({
    defaultValues: {
      title: '',
      publicationYear: '',
      journal: '',
      url: '',
      doi: '',
      abstract: '',
      researcherNotes: '',
    },
  })

  // Data loading effect
  useEffect(() => {
    const loadTags = async () => {
      try {
        setLoading(true)
        const tagsData = await fetchResearchTags()
        setTags(tagsData)
      } catch (error) {
        console.error('Error loading tags:', error)
        toast({
          title: 'Error loading tags',
          description: 'There was an error loading the research tags',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setLoading(false)
      }
    }

    loadTags()
  }, [toast])

  // Permission check effect
  useEffect(() => {
    if (!canManageResearch) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to add research articles',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      navigate('/research')
    }
  }, [canManageResearch, navigate, toast])

  // Tag management functions (identical to EditResearch)
  const handleAddNewTag = async () => {
    if (!newTagName.trim() || !user?.uid) return

    const trimmedName = newTagName.trim()

    // Check if tag already exists (case-insensitive)
    const existingTag = tags.find(
      (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase()
    )

    if (existingTag) {
      // If tag exists but isn't selected, select it
      if (!selectedTags.includes(existingTag.id)) {
        setSelectedTags((prev) => [...prev, existingTag.id])
        setNewTagName('')
        setShowTagError(false)

        toast({
          title: 'Tag already exists',
          description: `"${existingTag.name}" already exists and has been added to your selection`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'Tag already selected',
          description: `"${existingTag.name}" is already in your selection`,
          status: 'warning',
          duration: 3000,
          isClosable: true,
        })
      }
      return
    }

    try {
      setSaving(true)
      const newTagId = await addResearchTag({
        name: trimmedName,
        createdAt: new Date() as any,
        createdBy: user.uid,
      })

      const newTag: ResearchTag = {
        id: newTagId,
        name: trimmedName,
        createdAt: new Date() as any,
        createdBy: user.uid,
      }

      setTags((prev) => [...prev, newTag])
      setSelectedTags((prev) => [...prev, newTagId])
      setNewTagName('')
      setShowTagError(false)

      toast({
        title: 'Tag created',
        description: `"${trimmedName}" has been created and added`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error creating tag:', error)
      toast({
        title: 'Error creating tag',
        description: 'There was an error creating the new tag',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((id) => id !== tagId))
    setShowTagError(false)
  }

  const handleSelectTag = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      setSelectedTags((prev) => [...prev, tagId])
      setShowTagError(false)
    }
  }

  // Form submission (adapted for creation)
  const onSubmit = async (data: AddResearchFormData) => {
    if (!user?.uid) {
      toast({
        title: 'Error',
        description: 'You must be logged in to add research',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    if (selectedTags.length === 0) {
      setShowTagError(true)
      toast({
        title: 'Validation Error',
        description: 'Please select at least one tag',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    setShowTagError(false)

    try {
      setSaving(true)

      const researchData: Omit<Research, 'id'> = {
        title: data.title,
        publicationYear: data.publicationYear,
        journal: data.journal,
        url: data.url,
        ...(data.doi?.trim() && { doi: data.doi.trim() }),
        abstract: data.abstract,
        ...(data.researcherNotes?.trim() && {
          researcherNotes: data.researcherNotes.trim(),
        }),
        tags: selectedTags,
        createdAt: new Date() as any,
        createdBy: user.uid,
      }

      const researchId = await addResearch(researchData)

      toast({
        title: 'Research Added',
        description: 'The research article has been successfully added',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      navigate(`/research/${researchId}`)
    } catch (error) {
      console.error('Error adding research:', error)
      toast({
        title: 'Error',
        description: 'Failed to add research article',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return {
    // Data
    tags,
    selectedTags,
    newTagName,

    // State
    loading,
    saving,
    showTagError,
    canManageResearch,

    // Form
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,

    // Tag management
    setNewTagName,
    handleAddNewTag,
    handleRemoveTag,
    handleSelectTag,
    setShowTagError,

    // Navigation
    onCancel: () => navigate('/research'),
  }
}
